import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, 
  X, 
  AlertCircle, 
  CheckCircle, 
  Loader2,
  AlertTriangle,
  Crown,
  Users,
  Trash2
} from 'lucide-react';
import { getApiBaseUrl } from '../../utils/apiUrl';

const TeamImportModal = ({ isOpen, onClose, onImportSuccess }) => {
  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState('');
  const [jsonDataTopTeams, setJsonDataTopTeams] = useState('');
  const [jsonDataPopularTeams, setJsonDataPopularTeams] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [previewTop, setPreviewTop] = useState(null);
  const [previewPopular, setPreviewPopular] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchLeagues();
    }
  }, [isOpen]);

  const fetchLeagues = async () => {
    try {
      // ใช้ endpoint เดียวกับหน้า admin/leagues เพื่อให้ข้อมูลสัมพันธ์กัน
      const params = new URLSearchParams({
        page: '1',
        limit: '100',
        status: 'active', // แสดงเฉพาะลีกที่ active
        sortBy: 'name',
        sortOrder: 'asc'
      });

      const response = await fetch(`${getApiBaseUrl()}/api/leagues?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // ใช้โครงสร้างข้อมูลเดียวกับ LeagueManagement
        setLeagues(data.data.leagues || data.data || []);
      }
    } catch (err) {
      console.error('Error fetching leagues:', err);
      setError('ไม่สามารถโหลดข้อมูลลีกได้');
    }
  };

  const validateJson = (text) => {
    try {
      const parsed = JSON.parse(text);
      
      if (!Array.isArray(parsed)) {
        return { valid: false, error: 'JSON must be an array' };
      }

      if (parsed.length === 0) {
        return { valid: false, error: 'Array cannot be empty' };
      }

      // Validate structure
      for (let i = 0; i < parsed.length; i++) {
        const team = parsed[i];
        
        if (!team.pokemon || !Array.isArray(team.pokemon)) {
          return { valid: false, error: `Team ${i + 1}: Missing or invalid pokemon array` };
        }

        if (team.pokemon.length === 0 || team.pokemon.length > 3) {
          return { valid: false, error: `Team ${i + 1}: Must have 1-3 Pokemon` };
        }

        for (let j = 0; j < team.pokemon.length; j++) {
          const pokemon = team.pokemon[j];
          if (!pokemon.speciesId) {
            return { valid: false, error: `Team ${i + 1}, Pokemon ${j + 1}: Missing speciesId` };
          }
        }
      }

      return { valid: true, data: parsed };
    } catch (err) {
      return { valid: false, error: `Invalid JSON: ${err.message}` };
    }
  };

  const handleJsonChangeTopTeams = (value) => {
    setJsonDataTopTeams(value);
    setError(null);

    if (value.trim()) {
      const validation = validateJson(value);
      if (validation.valid) {
        setPreviewTop({
          count: validation.data.length
        });
      } else {
        setPreviewTop({ error: validation.error });
      }
    } else {
      setPreviewTop(null);
    }
  };

  const handleJsonChangePopularTeams = (value) => {
    setJsonDataPopularTeams(value);
    setError(null);

    if (value.trim()) {
      const validation = validateJson(value);
      if (validation.valid) {
        setPreviewPopular({
          count: validation.data.length
        });
      } else {
        setPreviewPopular({ error: validation.error });
      }
    } else {
      setPreviewPopular(null);
    }
  };

  const handleImport = async () => {
    if (!selectedLeagueId) {
      setError('กรุณาเลือกลีก');
      return;
    }

    if (!jsonDataTopTeams.trim() && !jsonDataPopularTeams.trim()) {
      setError('กรุณาใส่ข้อมูล JSON อย่างน้อย 1 ช่อง');
      return;
    }

    // Validate both JSON inputs
    let allTeams = [];
    let hasError = false;
    let errorMessage = '';

    // Process Top Teams
    if (jsonDataTopTeams.trim()) {
      const validation = validateJson(jsonDataTopTeams);
      if (!validation.valid) {
        hasError = true;
        errorMessage = `Top Teams: ${validation.error}`;
      } else {
        // Ensure weight >= 1.0 for Top Teams
        const topTeams = validation.data.map(team => ({
          ...team,
          weight: Math.max(parseFloat(team.weight) || 1.0, 1.0)
        }));
        allTeams = [...allTeams, ...topTeams];
      }
    }

    // Process Popular Teams
    if (jsonDataPopularTeams.trim() && !hasError) {
      const validation = validateJson(jsonDataPopularTeams);
      if (!validation.valid) {
        hasError = true;
        errorMessage = `Trending Teams: ${validation.error}`;
      } else {
        // Ensure weight < 1.0 for Popular Teams
        const popularTeams = validation.data.map(team => ({
          ...team,
          weight: Math.min(parseFloat(team.weight) || 0.5, 0.99)
        }));
        allTeams = [...allTeams, ...popularTeams];
      }
    }

    if (hasError) {
      setError(errorMessage);
      return;
    }

    if (allTeams.length === 0) {
      setError('ไม่มีข้อมูลทีมที่จะนำเข้า');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/teams/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          leagueId: parseInt(selectedLeagueId),
          teams: allTeams,
          replaceExisting: replaceExisting
        })
      });

      const data = await response.json();

      if (data.success) {
        const imported = data.data.imported || 0;
        const verified = data.data.verified || 0;
        
        setSuccess({
          imported: imported,
          skipped: data.data.skipped || 0,
          verified: verified,
          errors: data.data.errors
        });
        
        console.log('[IMPORT] Import result:', {
          imported,
          verified,
          leagueId: data.data.leagueId
        });
        
        // Reset form after 2 seconds
        setTimeout(() => {
          setJsonDataTopTeams('');
          setJsonDataPopularTeams('');
          setSelectedLeagueId('');
          setPreviewTop(null);
          setPreviewPopular(null);
          if (onImportSuccess) {
            onImportSuccess();
          }
          onClose();
        }, 2000);
      } else {
        setError(data.error || 'การนำเข้าล้มเหลว');
      }
    } catch (err) {
      setError('เกิดข้อผิดพลาดในการเชื่อมต่อ: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  นำเข้าทีมแนะนำ
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  วาง JSON code เพื่อนำเข้าทีม (รองรับ format จาก PvPoke, PvPIV, etc.)
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* League Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                เลือกลีก <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedLeagueId}
                onChange={(e) => setSelectedLeagueId(e.target.value)}
                disabled={loading}
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50"
              >
                <option value="">-- เลือกลีก --</option>
                {leagues.map((league) => (
                  <option key={league.id} value={league.id}>
                    {league.name_th || league.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Replace Existing Option */}
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <input
                type="checkbox"
                id="replaceExisting"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                disabled={loading}
                className="w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="replaceExisting" className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">แทนที่ข้อมูลเดิม</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">
                  หากเลือก จะลบทีมทั้งหมดของลีกนี้ก่อนนำเข้าข้อมูลใหม่
                </span>
              </label>
            </div>

            {/* JSON Input - Two Columns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Top Teams */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center space-x-2">
                      <Crown className="w-4 h-4 text-yellow-600" />
                      <span>Top Teams</span>
                    </div>
                  </label>
                  {jsonDataTopTeams && (
                    <button
                      onClick={() => {
                        setJsonDataTopTeams('');
                        setPreviewTop(null);
                      }}
                      disabled={loading}
                      className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 flex items-center space-x-1 transition-colors disabled:opacity-50"
                      title="Clear"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  วาง JSON code ตรงนี้ (weight ≥ 1.0)
                </div>
                <textarea
                  value={jsonDataTopTeams}
                  onChange={(e) => handleJsonChangeTopTeams(e.target.value)}
                  onPaste={(e) => {
                    // Auto-format pasted JSON
                    const pastedText = e.clipboardData.getData('text');
                    try {
                      const parsed = JSON.parse(pastedText);
                      const formatted = JSON.stringify(parsed, null, 2);
                      e.preventDefault();
                      handleJsonChangeTopTeams(formatted);
                    } catch {
                      // If not valid JSON, paste as-is
                    }
                  }}
                  disabled={loading}
                  placeholder='วาง JSON code ตรงนี้:&#10;[{"pokemon":[{"speciesId":"malamar","fastMove":"PSYWAVE","chargedMoves":["SUPER_POWER","FOUL_PLAY"]}],"weight":1}]'
                  className="w-full h-64 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent disabled:opacity-50 resize-none"
                />
                
                {/* Preview Top Teams */}
                {previewTop && !previewTop.error && (
                  <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                      <span className="text-yellow-800 dark:text-yellow-200 font-medium">
                        พบ {previewTop.count} ทีม
                      </span>
                    </div>
                  </div>
                )}
                
                {previewTop && previewTop.error && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start space-x-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-red-800 dark:text-red-200 text-xs">{previewTop.error}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Trending Teams */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-blue-600" />
                      <span>Trending Teams</span>
                    </div>
                  </label>
                  {jsonDataPopularTeams && (
                    <button
                      onClick={() => {
                        setJsonDataPopularTeams('');
                        setPreviewPopular(null);
                      }}
                      disabled={loading}
                      className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 flex items-center space-x-1 transition-colors disabled:opacity-50"
                      title="Clear"
                    >
                      <Trash2 className="w-3 h-3" />
                      <span>Clear</span>
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  วาง JSON code ตรงนี้ (weight &lt; 1.0)
                </div>
                <textarea
                  value={jsonDataPopularTeams}
                  onChange={(e) => handleJsonChangePopularTeams(e.target.value)}
                  onPaste={(e) => {
                    // Auto-format pasted JSON
                    const pastedText = e.clipboardData.getData('text');
                    try {
                      const parsed = JSON.parse(pastedText);
                      const formatted = JSON.stringify(parsed, null, 2);
                      e.preventDefault();
                      handleJsonChangePopularTeams(formatted);
                    } catch {
                      // If not valid JSON, paste as-is
                    }
                  }}
                  disabled={loading}
                  placeholder='วาง JSON code ตรงนี้:&#10;[{"pokemon":[{"speciesId":"malamar","fastMove":"PSYWAVE","chargedMoves":["SUPER_POWER","FOUL_PLAY"]}],"weight":1}]'
                  className="w-full h-64 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 resize-none"
                />
                
                {/* Preview Popular Teams */}
                {previewPopular && !previewPopular.error && (
                  <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      <span className="text-blue-800 dark:text-blue-200 font-medium">
                        พบ {previewPopular.count} ทีม
                      </span>
                    </div>
                  </div>
                )}
                
                {previewPopular && previewPopular.error && (
                  <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start space-x-2 text-sm">
                      <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                      <span className="text-red-800 dark:text-red-200 text-xs">{previewPopular.error}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Global Error */}
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-start space-x-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-red-800 dark:text-red-200">{error}</span>
                </div>
              </div>
            )}

            {/* Global Success */}
            {success && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                      <span className="text-green-800 dark:text-green-200 font-medium">
                        นำเข้าสำเร็จ: {success.imported} ทีม
                      </span>
                    </div>
                    {success.verified !== undefined && (
                      <div className="text-blue-800 dark:text-blue-200 text-sm">
                        ตรวจสอบในฐานข้อมูล: {success.verified} ทีม
                      </div>
                    )}
                    {success.skipped > 0 && (
                      <div className="text-yellow-800 dark:text-yellow-200">
                        ข้าม: {success.skipped} ทีม
                      </div>
                    )}
                  {success.errors && success.errors.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {success.errors.slice(0, 5).map((err, idx) => (
                        <div key={idx} className="text-xs text-red-600 dark:text-red-400">
                          • {err}
                        </div>
                      ))}
                      {success.errors.length > 5 && (
                        <div className="text-xs text-gray-500">
                          และอีก {success.errors.length - 5} ข้อผิดพลาด...
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              ยกเลิก
            </button>
            <button
              onClick={handleImport}
              disabled={loading || !selectedLeagueId || (!jsonDataTopTeams.trim() && !jsonDataPopularTeams.trim()) || !!error}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>กำลังนำเข้า...</span>
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  <span>นำเข้า</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeamImportModal;
